import path from 'path'
import { TypedEmitter } from 'tiny-typed-emitter'
import { AudioEngineDevSettings } from '../../AppContext'
import { logger } from '../../services/Logger'
import { AudioSourceInfo, AudioInputSettings, AudioTrackWithInfo, AudioOutputSettings } from '../../UserContext'
import { getAssetPath } from '../../utils'
import FakeMediaStreamTrack from './FakeMediaStreamTrack'
import { IAudioEngine, AudioEngineEventEmitter, AudioEngineImpl } from './IAudioEngine'
import { AudioEngineAudioDeviceInfo, AudioEngineError, AudioEngineOutputDeviceType } from './types'
import { WebAudioEngineRoom } from './WebAudioEngineRoom'

const log = logger.createChildLogger({ name: 'WebAudioEngine' })

export class WebAudioEngine extends TypedEmitter<AudioEngineEventEmitter> implements IAudioEngine {
    private receiverWorker: Worker | undefined
    private audioContext: AudioContext
    private _masterStreamNode: MediaStreamAudioDestinationNode
    private currentTargetSampleRate: number
    private currentRoom: WebAudioEngineRoom | undefined
    private sampleRateChangeInterval: undefined | number
    private workletInitialized: Promise<boolean> | null = null

    constructor() {
        super()
        this.audioContext = new AudioContext()
        this._masterStreamNode = this.audioContext.createMediaStreamDestination()
        /* @ts-ignore */
        window.webAeAudioContext = this.audioContext
        this.currentTargetSampleRate = this.audioContext.sampleRate
    }

    async init(): Promise<void> {
        const assetPath = getAssetPath()
        this.workletInitialized = new Promise<boolean>(async (resolve) => {
            log.verbose('init', 'assetPath=' + assetPath)

            const processorPath = path.join(assetPath, 'audio-processor.js')
            await this.audioContext.audioWorklet.addModule(`${processorPath}?_=${Date.now()}`).catch((err) => {
                log.error('WebAudioEngine', 'Failed to load audio worklet module', { err })
                this.emit(
                    'onFatalError',
                    new AudioEngineError('audio_worklet_error', 'The audio worklet module failed to load')
                )
            })

            resolve(true)
        })

        const receiverPath = path.join(assetPath, 'receiver-worker.js')
        this.receiverWorker = new Worker(receiverPath, { type: 'module' })
        this.receiverWorker.postMessage({ type: 'init', payload: { targetSampleRate: this.audioContext.sampleRate } })
        this.receiverWorker.addEventListener('message', (ev) => {
            if (ev.data.type === 'initSuccess') {
                this.emit('onInitializationSuccess')
            } else if (ev.data.type === 'error') {
                this.emit('onFatalError', ev.data.error)
            } else if (ev.data.type === 'connectionStateChange') {
                this.emit('onNetworkStatusChanged', ev.data.state)
            } else if (ev.data.type === 'log') {
                switch (ev.data.level) {
                    case 'verbose':
                        log.verbose('ReceiverWorker', ev.data.msg, ev.data.vars)
                        break
                    case 'info':
                        log.info('ReceiverWorker', ev.data.msg, ev.data.vars)
                        break
                    case 'checkpoint':
                        log.checkpoint('ReceiverWorker', ev.data.msg, ev.data.vars)
                        break
                    case 'warn':
                        log.warning('ReceiverWorker', ev.data.msg, ev.data.vars)
                        break
                    case 'error':
                        log.error('ReceiverWorker', ev.data.msg, ev.data.vars)
                        break
                }
            }
        })

        this.sampleRateChangeInterval = window.setInterval(() => {
            if (this.audioContext && this.receiverWorker) {
                if (this.audioContext.sampleRate !== this.currentTargetSampleRate) {
                    log.verbose(
                        'WebAudioEngine',
                        `detected sample rate change ${this.audioContext.sampleRate} != ${this.currentTargetSampleRate}`
                    )
                    this.currentTargetSampleRate = this.audioContext.sampleRate
                    this.receiverWorker.postMessage({
                        type: 'setTargetSampleRate',
                        payload: { sampleRate: this.currentTargetSampleRate },
                    })
                }
            }
        }, 1000)
    }

    createRoom(): WebAudioEngineRoom {
        if (!this.receiverWorker || !this.audioContext || !this.workletInitialized) {
            throw new Error('webae: failed to create room, web audio engine is still initializing')
        }

        if (this.currentRoom) {
            return this.currentRoom
        }

        log.verbose('WebAudioEngine', 'creating new WebAudioEngineRoom')
        const room = new WebAudioEngineRoom(
            this.receiverWorker!,
            this.audioContext,
            this._masterStreamNode,
            this.workletInitialized!
        )
        this.currentRoom = room
        return room
    }

    getCurrentRoom(): WebAudioEngineRoom | null {
        return this.currentRoom ?? null
    }

    async setSettings(settings: AudioEngineDevSettings, authToken?: string): Promise<boolean> {
        return true
    }

    async getAudioDevices(): Promise<AudioEngineAudioDeviceInfo[] | null> {
        return null
    }

    checkSupportedSampleRates(params: {
        audioEngineDeviceId: string
        sampleRates: number[]
        deviceDirection: 'input' | 'output'
    }): Promise<number[]> {
        throw new Error('Web audio engine does not suport sending pro audio')
    }

    getAudioStream(
        source: AudioSourceInfo,
        settings: AudioInputSettings
    ): Promise<{ track: AudioTrackWithInfo; modifiedSettings: Partial<AudioInputSettings> }> {
        throw new Error('Web audio engine does not suport sending pro audio')
    }

    startRemoteRecording(
        streamId: string,
        channelGroup: number | null,
        outputFileFullPath: string | null,
        source: AudioSourceInfo | null,
        settings: AudioOutputSettings | null
    ): void {
        if (this.currentRoom) {
            this.currentRoom.startRemoteRecording(streamId, channelGroup, outputFileFullPath, source, settings)
        }
    }

    stopRemoteRecording(streamId: string, channelGroup: number) {
        if (this.currentRoom) {
            this.currentRoom.stopRemoteRecording(streamId, channelGroup)
        }
    }

    startLocalRecording(streamId: string, filePath: string, channelGroup?: number | undefined): void {
        throw new Error('Web audio engine does not support local recording')
    }

    stopLocalRecording(streamId: string, channelGroup?: number | undefined): void {
        throw new Error('Web audio engine does not support local recording')
    }

    async setAudioOutputDevice(
        source: AudioSourceInfo | null,
        _settings: AudioOutputSettings | null,
        outputIdx?: AudioEngineOutputDeviceType
    ): Promise<boolean> {
        if (outputIdx !== undefined && outputIdx !== AudioEngineOutputDeviceType.PA_PRIMARY) {
            throw new Error(
                `setAudioOutputDevice was called on web audio engine with an outputIdx (${outputIdx}) other than PA_PRIMARY.`
            )
        }

        if ('setSinkId' in AudioContext.prototype) {
            try {
                if (source?.deviceId) {
                    if (source.deviceId === 'default') {
                        // @ts-ignore
                        await this.audioContext.setSinkId('')
                    } else {
                        // @ts-ignore
                        await this.audioContext.setSinkId(source.deviceId)
                    }
                } else {
                    // @ts-ignore
                    await this.audioContext.setSinkId({ type: 'none' })
                }
            } catch (e) {
                if (e instanceof Error) {
                    if (e.name === 'NotFoundError') {
                        // NOTE: This resolves an obsecure bug where the initial
                        // audio context fails to set the audio output.
                        this.resetAudioContext()
                    }
                }
                log.error('WebAudioEngine', `Failed to set audio context sink to ${source?.deviceId}`, { err: e })
            }
        }
        return true
    }

    setAudioLoopbackStream(track: AudioTrackWithInfo | FakeMediaStreamTrack, outputIdx: number): Promise<boolean> {
        throw new Error('Web audio engine does not support audio loopback')
    }

    unsetAudioLoopbackStream(track: AudioTrackWithInfo): Promise<boolean> {
        throw new Error('Web audio engine does not support audio loopback')
    }

    startMasterCaptureToFile(filePath: string): Promise<boolean> {
        throw new Error('Web audio engine does not support cature to file.')
    }

    stopMasterCaptureToFile(): Promise<boolean> {
        throw new Error('Web audio engine does not support cature to file.')
    }

    quit(): void {
        if (this.receiverWorker) {
            this.receiverWorker.postMessage({ type: 'disconnect' })
        }
    }

    testCrash(): Promise<boolean> {
        throw new Error('Method not implemented.')
    }

    async resetAudioContext() {
        // Destory the current audio context and receiver worker
        if (this.currentRoom) {
            await this.currentRoom.destroy()
            this.currentRoom = undefined
        }
        if (this.receiverWorker) {
            this.receiverWorker.terminate()
            clearInterval(this.sampleRateChangeInterval)
        }
        this.audioContext.suspend()
        this.audioContext.close()

        // Make a new audio context and receiver worker
        this.audioContext = new AudioContext()
        this._masterStreamNode = this.audioContext.createMediaStreamDestination()
        /* @ts-ignore */
        window.webAeAudioContext = this.audioContext
        this.currentTargetSampleRate = this.audioContext.sampleRate
        await this.init()
    }

    get implementation(): AudioEngineImpl {
        return 'web'
    }

    get masterStreamNode(): MediaStreamAudioDestinationNode {
        return this._masterStreamNode
    }
}
